import React from 'react';
import { View, Text, TextInput, Pressable, ActivityIndicator, Platform } from 'react-native';
import { signInStyles } from '../../src/signIn/styles/signIn.styles';
import { SignInAuthScreenLayout } from '../../src/signIn/components/SignInAuthScreenLayout';
import { verificationFormStyles as vf } from './VerificationForm.styles';
import { WEB_HOVER_TRANSITION_STYLE } from '../../src/shared/constants/animations';

interface VerificationFormProps {
  email: string;
  verificationCode?: string;
  setVerificationCode?: (code: string) => void;
  loading?: boolean;
  onVerify?: () => void;
  onBack: () => void;
  title?: string;
  subtitle?: string;
  hint?: string;
  buttonText?: string;
  backButtonText?: string;
  error?: string;
  /**
   * 'code' = user enters a 6-digit code (OTP).
   * 'link' = user clicks a link in email (Supabase default); no code input shown.
   */
  confirmationMode?: 'code' | 'link';
  onResend?: () => void;
  resendLoading?: boolean;
  testID?: string;
}

const SIGN_IN_PLACEHOLDER_COLOR = '#5A5A60';
const SIGN_IN_ACCENT_SPINNER_COLOR = '#A78BFA';

const LINK_MODE_DEFAULTS = {
  title: 'Confirm your email',
  subtitle: (email: string) => `We sent a confirmation link to ${email}`,
  hint: 'Click the link in the email to verify your account. You can close this page and return after confirming.',
};

export const VerificationForm: React.FC<VerificationFormProps> = ({
  email,
  verificationCode = '',
  setVerificationCode,
  loading = false,
  onVerify,
  onBack,
  title,
  subtitle,
  hint,
  buttonText = 'Verify Email',
  backButtonText = 'Back to Sign Up',
  error,
  confirmationMode = 'code',
  onResend,
  resendLoading = false,
  testID,
}) => {
  const isLinkMode = confirmationMode === 'link';
  const resolvedTitle = title ?? (isLinkMode ? LINK_MODE_DEFAULTS.title : 'Verify Your Email');
  const resolvedSubtitle = subtitle ?? (isLinkMode ? LINK_MODE_DEFAULTS.subtitle(email) : `We sent a verification code to ${email}`);
  const resolvedHint = hint ?? (isLinkMode ? LINK_MODE_DEFAULTS.hint : 'Please check your inbox and enter the code below');

  // SECURITY: Only allow digits (0-9) in verification code
  const handleCodeChange = (text: string) => {
    const digitsOnly = text.replace(/[^0-9]/g, '');
    setVerificationCode?.(digitsOnly.slice(0, 6));
  };

  return (
    <SignInAuthScreenLayout testID={testID || 'verification-form'}>
      <View
        style={[signInStyles.card, vf.verificationCard]}
        testID={testID ? `${testID}-card` : 'verification-form-card'}
      >
          <Text style={signInStyles.title}>{resolvedTitle}</Text>
          <Text style={signInStyles.subtitle}>{resolvedSubtitle}</Text>
          <Text style={vf.verificationHint}>{resolvedHint}</Text>

          {!isLinkMode && (
            <>
              <View style={signInStyles.inputContainer}>
                <TextInput
                  style={[signInStyles.input, error ? signInStyles.inputError : undefined]}
                  placeholder="Verification Code"
                  placeholderTextColor={SIGN_IN_PLACEHOLDER_COLOR}
                  value={verificationCode}
                  onChangeText={handleCodeChange}
                  autoCapitalize="none"
                  keyboardType="number-pad"
                  maxLength={6}
                  autoFocus
                  testID={testID ? `${testID}-code-input` : 'verification-form-code-input'}
                />
                {error ? (
                  <View style={signInStyles.errorContainer}>
                    <Text style={signInStyles.errorText}>{error}</Text>
                  </View>
                ) : null}
              </View>

              <Pressable
                style={({ pressed, hovered }) => [
                  signInStyles.button,
                  signInStyles.primaryButton,
                  Platform.select({ web: WEB_HOVER_TRANSITION_STYLE as never }),
                  pressed && signInStyles.primaryButtonPressed,
                  hovered && Platform.OS === 'web' && signInStyles.primaryButtonHover,
                ]}
                onPress={onVerify}
                disabled={loading || !verificationCode}
                testID={testID ? `${testID}-verify-button` : 'verification-form-verify-button'}
              >
                {loading
                  ? <ActivityIndicator color={SIGN_IN_ACCENT_SPINNER_COLOR} />
                  : <Text style={signInStyles.buttonText}>{buttonText}</Text>
                }
              </Pressable>
            </>
          )}

          {isLinkMode && onResend && (
            <Pressable
              style={({ pressed }) => [signInStyles.linkButton, pressed && signInStyles.linkButtonPressed]}
              onPress={onResend}
              disabled={resendLoading}
              testID={testID ? `${testID}-resend-button` : 'verification-form-resend-button'}
            >
              {resendLoading
                ? <ActivityIndicator size="small" color={SIGN_IN_ACCENT_SPINNER_COLOR} />
                : <Text style={signInStyles.linkText}>Resend email</Text>
              }
            </Pressable>
          )}

          <Pressable
            style={({ pressed }) => [signInStyles.linkButton, pressed && signInStyles.linkButtonPressed]}
            onPress={onBack}
            testID={testID ? `${testID}-back-button` : 'verification-form-back-button'}
          >
            <Text style={signInStyles.linkText}>{backButtonText}</Text>
          </Pressable>
      </View>
    </SignInAuthScreenLayout>
  );
};
